import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { TextField, Typography, Alert, AlertTitle } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import getErrorMessage from '../../../Helpers/Errors/getErrorMessage';
import { ColumnFlexBox } from '../../../Components/FlexBox';
import InputPassword from '../Components/InputPassword';

const useStyles = () => ({
  lgtextfield: {
    mb: '16px',
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      '& fieldset': {
        border: '2px solid #d6d6d6',
      },
    },
    helpertxt: {
      marginLeft: 'unset',
    },
  },
  fieldCaption: {
    fontSize: '1rem',
  },
});

export const checkIfPasswordIsExpired = (FetchError) =>
  FetchError?.payload?.errors?.filter(
    (x) => typeof x === 'object' && x.code === 'PASSWORD_EXPIRED'
  ).length > 0;

const FirstLogin = function FirstLogin({ loginError = null }) {
  const classes = useStyles();
  const intl = useIntl();
  const [subtitles, setSubtitles] = useState([]);
  const [subColor, setSubColor] = useState('textPrimary');
  const [needToChangePassword, setNeedToChangePassword] = useState(false);
  const {
    register,
    formState: { errors },
    reset,
  } = useFormContext();

  useEffect(() => {
    if (loginError?.payload) {
      setSubColor('error.main');
      if (checkIfPasswordIsExpired(loginError)) {
        setNeedToChangePassword(true);
      }
      setSubtitles(
        getErrorMessage(loginError, intl).split(',') || [
          intl.messages.LOGIN_ERROR,
        ]
      );
    }
  }, [loginError]);

  useEffect(() => {
    //clears the submit for change password form validation but maintains the inserted data
    reset(undefined, { keepValues: true });
  }, [needToChangePassword]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <Typography variant="h4" color="textPrimary">
        {needToChangePassword
          ? intl.messages.CHANGE_PASSWORD
          : intl.messages.LOGIN}
      </Typography>
      {subtitles.length > 0 ? (
        <Alert
          severity="error"
          sx={{ border: '2px solid', borderColor: 'error.main' }}
        >
          <AlertTitle>{intl.messages.LOGIN_ERROR_TITLE}</AlertTitle>
          <ColumnFlexBox gap="16px">
            {subtitles.map((sub) => (
              <Typography
                key={sub}
                variant="subtitle1"
                sx={{
                  width: '100%',
                  color: subColor,
                }}
              >
                {intl.messages[sub] || sub}
              </Typography>
            ))}
          </ColumnFlexBox>
        </Alert>
      ) : (
        <Typography variant="subtitle1" sx={{ mb: '24px' }}>
          {intl.messages.ACCESS_WITH_CREDENTIALS}
        </Typography>
      )}
      {!needToChangePassword && (
        <>
          <Typography
            variant="subtitle2"
            sx={classes.fieldCaption}
            color="textSecondary"
          >
            {intl.messages.USERNAME}
          </Typography>
          <TextField
            sx={classes.lgtextfield}
            {...register('username', {
              required: intl.messages.MANDATORY_FIELD,
            })}
            variant="outlined"
            fullWidth
            helperText={errors?.username?.message}
            error={errors?.username}
          />
          <InputPassword
            label={intl.messages.PASSWORD}
            register={register}
            name="password"
            error={errors?.password}
            fullWidth
          />
        </>
      )}
      {needToChangePassword && (
        <>
          <InputPassword
            label={intl.messages.NEW_PASSWORD}
            register={register}
            name="newPassword"
            error={errors?.newPassword}
            fullWidth
          />
          <InputPassword
            label={intl.messages.CONFIRM_PASSWORD}
            register={register}
            name="confirmPassword"
            error={errors?.confirmPassword}
            fullWidth
            customValidation={(value, formValues) =>
              value !== formValues?.newPassword
                ? intl.messages.PASSWORD_MISMATCH
                : true
            }
          />
        </>
      )}
    </div>
  );
};

export default FirstLogin;
